<template>
	<b-row no-gutters>
        <b-col>
            <b-row align-h="center" style="margin-top: 40px;">
                <span style="font-size: 1.1em; font-weight: bold;">{{ $t('scopeBarChartTitle') }}</span>
            </b-row>
            <b-row v-if="hasAnyValueToRenderBarChart" style="margin-top: 50px">
                <b-col>
                    <div style="width: 45em; height: 45em; margin: auto;">
                        <bar-chart :chart-data="barChartData" :options="barChartOptions" :chart-id="barChartId"/>
                    </div>
                </b-col>
                <b-col>
                    <div class="table-responsive" style="margin-top: 10vh">
                        <table v-if="scopeReport" class="table table-hover" style="table-layout: fixed; font-size: 0.875em;">
                            <thead style="text-align: center">
                            <tr>
                                <th style="text-align: left; width: 16em">Scope</th>
                                <th style="text-align: right">{{this.year - 1}}</th>
                                <th style="text-align: right">{{this.year}}</th>
                                <th>{{$t('changingColumn')}}</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="tag, i in scopeTags" :key="i">
                                <td style="text-align: left">{{tag.displayName}} ({{$t('ton')}}CO2e)</td>
                                <td style="text-align: right">{{scopeReport.past[tag.id] | parseNumber('float', locale) | convertZeroValueToDash(locale)}}</td>
                                <td style="text-align: right">{{scopeReport.current[tag.id] | parseNumber('float', locale) | convertZeroValueToDash(locale)}}</td>
                                <td style="text-align: right">
                                    <b-row no-gutters align-h="between" v-if="calculateRatio(scopeReport.past[tag.id], scopeReport.current[tag.id]) !== '0.00'">
                                        <b-col cols="6" v-if="calculateRatio(scopeReport.past[tag.id], scopeReport.current[tag.id])">
                                            <b-row no-gutters>
                                                <b-col cols="11" style="text-align: right">
                                                    <img v-if="calculateRatio(scopeReport.past[tag.id], scopeReport.current[tag.id]) < 0" width="16px" height="16px" src="../../../../assets/icon/down-round-g.svg"/>
                                                    <img v-else width="16px" height="16px" src="../../../../assets/icon/up_round_r.svg"/>
                                                </b-col>
                                            </b-row>
                                        </b-col>
                                        <b-col cols="6" style="text-align: left">
                                            {{ calculateRatio(scopeReport.past[tag.id], scopeReport.current[tag.id]) | absoluteNumber | parseNumber('float', locale) | convertZeroValueToDash(locale) }}
                                        </b-col>
                                    </b-row>
                                    <b-row no-gutters align-h="end" v-else>
                                        <b-col cols="6" style="text-align: left">-</b-col>
                                    </b-row>
                                </td>
                            </tr>
                            <tr>
                                <th style="text-align: left">{{$t('total')}} ({{$t('ton')}}CO2e)</th>
                                <th style="text-align: right">{{scopeReport.past.total | parseNumber('float', locale) | convertZeroValueToDash(locale)}}</th>
                                <th style="text-align: right">{{scopeReport.current.total  | parseNumber('float', locale) | convertZeroValueToDash(locale)}}</th>
                                <th>
                                    <b-row no-gutters align-h="between" v-if="calculateRatio(scopeReport.past.total, scopeReport.current.total) !== '0.00'">
                                        <b-col cols="6">
                                            <b-row no-gutters>
                                                <b-col cols="11" style="text-align: right">
                                                    <img v-if="calculateRatio(scopeReport.past.total, scopeReport.current.total) < 0" width="16px" height="16px" src="../../../../assets/icon/down-round-g.svg"/>
                                                    <img v-else width="16px" height="16px" src="../../../../assets/icon/up_round_r.svg"/>
                                                </b-col>
                                            </b-row>
                                        </b-col>
                                        <b-col cols="6" style="text-align: left">
                                            {{ calculateRatio(scopeReport.past.total, scopeReport.current.total) | absoluteNumber | parseNumber('float', locale) }}
                                        </b-col>
                                    </b-row>
                                    <b-row no-gutters align-h="end" v-else>
                                        <b-col cols="6" style="text-align: left">-</b-col>
                                    </b-row>
                                </th>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </b-col>
            </b-row>
            <b-row v-else>
                <b-col>
                    <span class="float-left">{{$t('scopeComparingError')}}</span>
                </b-col>
            </b-row>
        </b-col>
    </b-row>
</template>

<script>
import BarChart from '../../../../components/charts/BarChart';
import globalFilters from '../../../../filters';
import uuidV4 from 'uuid/v4';

export default {
    name: 'ScopeComparison',
    components: {BarChart},
    props: {
        initialScopeReport: {
            type: Object,
            default() {
                return {};
            }
        },
        initialEmissions: {
            default() {
                return {
                    current: [],
                    past: []
                };
            }
        },
        selectedSiteId: {
            default() {
                return null;
            }
        },
        selectedSiteGroupId: {
            default() {
                return null;
            }
        }
    },
    data() {
        return {
            barChartId: uuidV4(),
            colourPalette: [
                'rgba(250,164,58,1)',
                'rgba(96,189,104,1)',
                'rgba(247,82,133,1)',
                'rgba(93,165,218,1)',
                'rgba(222,207,63,1)',
                'rgba(178,118,178,1)',
                'rgba(77,77,77,1)',
                'rgba(178,145,47,1)',
                'rgba(222,207,63,1)',
                'rgba(241,88,84,1)'
            ],
            barChartData: {
                labels: [],
                datasets: []
            },
            barChartOptions: {
                legend: {
                    position: 'bottom'
                },
                title: {
                    display: false,
                    fontColor: '#555',
                    text: this.$t('comparingScope'),
                    fontSize: 16,
                    padding: 15
                },
                barValueSpacing: 20,
                scales: {
                    xAxes: [
                        {
                            gridLines: {
                                display: false
                            }
                        }
                    ],
                    yAxes: [
                        {
                            gridLines: {
                                display: false
                            },
                            ticks: {
                                min: 0
                            }
                        }
                    ]
                },
                tooltips: {
                    callbacks: {
                        label: (tooltipItem, data) => {
                            let label = data.datasets[tooltipItem.datasetIndex].label || '';
                            if (label) {
                                label += ': ';
                            }

                            return (
                                label +
                                globalFilters.parseNumber(
                                    Number(
                                        data.datasets[tooltipItem.datasetIndex].data[
                                            tooltipItem.index
                                        ].toFixed(2)
                                    ),
                                    'float',
                                    this.locale
                                ) +
                                ` tCO2`
                            );
                        }
                    }
                }
            },
            scopeReport: {
                current: [],
                past: [],
                scopeTags: []
            },
            scopeTags: []
        };
    },
    methods: {
        calculateRatio(lastValue, currentValue) {
            if (!lastValue) {
                return '0.00';
            }
            let subtraction = currentValue - lastValue;
            let ratio = subtraction / lastValue;
            return (ratio * 100).toFixed(2);
        },
        prepareScopeReportDataSet(report) {
            // Resets barChartData
            this.barChartData = {
                labels: [],
                datasets: []
            };
            this.barChartData.labels = [
                ...this.scopeTags.map(tag => {
                    return tag.displayName;
                })
            ];
            // Iterates for all report type ('current' and 'past')
            for (let key in report) {
                let data = [];
                // Prepares scope 1, 2, 3 ...
                this.scopeTags.forEach(tag => {
                    data.push(report[key][tag.id]);
                });

                this.barChartData.datasets.push({
                    label:
                        this.year -
                        (Object.keys(report).length - (Object.keys(report).indexOf(key) + 1)), // Sets label display order
                    backgroundColor: this.colourPalette[Object.keys(report).indexOf(key) % 10],
                    data: data
                });
            }
            // Rerenders piechart
            this.$eventHub.$emit(this.barChartId);
        },
        hasAnyValueToRenderBarChart() {
            let total = 0;
            for (let keyScope in this.scopeReport) {
                for (let key in this.scopeReport[keyScope]) {
                    total += this.scopeReport[keyScope][key];
                }
            }
            return total;
        },
        processScopeReport(data) {
            this.scopeTags = data.scopeTags;
            this.scopeReport = {
                past: data.past,
                current: data.current
            };
            this.prepareScopeReportDataSet(this.scopeReport);
        }
    },
    watch: {
        initialScopeReport(data) {
            this.processScopeReport(data);
        },
        selectedSiteId() {
            this.processScopeReport(this.initialScopeReport);
        },

        selectedSiteGroupId() {
            this.processScopeReport(this.initialScopeReport);
        }
    },
    mounted() {
        this.processScopeReport(this.initialScopeReport);
    }
};
</script>

<style scoped>
</style>

<i18n src="../../../../i18n/common.json"></i18n>

<i18n>
    {
        "tr": {
            "scopeComparingError": "Kapsam verisi bulunamadı",
            "changingColumn": "Değişim (%)",
            "scopeBarChartTitle": "Kapsam Kıyaslaması"
        },
        "en": {
            "scopeComparingError": "No scope data",
            "changingColumn": "Change (%)",
            "scopeBarChartTitle": "Scope Comparison"
        }
    }
</i18n>



// WEBPACK FOOTER //
// src/views/Analysis/Tabs/components/ScopeComparison.vue